import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { submitHomework } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useContext, useState } from "react";
import { Task } from "../constants/AllTypes";
import { LessonContext } from "../context/LessonContext";

type SendHomeworkOptions = {
  // For tasks that submit by themselves when the game ends — the student has no
  // Submit button to try again with, so retry once if the request never got out.
  retryOnNetworkError?: boolean;
};

const RETRY_DELAY_MS = 2000;

// Tasks with a submission request in flight, shared across every task component,
// so a double-click can't send twice and use up two of the student's attempts.
const inFlight = new Set<string>();

// No response and not a timeout: the request almost certainly never reached the
// server, so sending it again won't use up another attempt. A timeout is not
// retried — the server may have saved it and just been slow to answer.
const isNetworkError = (error: unknown) =>
  axios.isAxiosError(error) && !error.response && error.code === 'ERR_NETWORK';

export const useSendHomework = ({ retryOnNetworkError = false }: SendHomeworkOptions = {}) => {
    const queryClient = useQueryClient();
    const { setHomeworkSubmitted, setHomeworkSubmitFailed } = useContext(LessonContext);
    const { currentUser } = useContext(AuthContext);
    const [coinAnimationData, setCoinAnimationData] = useState<{
      currentCoins: number;
      newCoins: number;
    } | null>(null);

    const mutation = useMutation({
      mutationFn: async (data: { taskId: string; submit: any; result: string }) => {
        return await submitHomework(data);
      },
      onSuccess: () => {
        // Refresh homework list automatically
        queryClient.invalidateQueries({ queryKey: ['studentHomework', currentUser?.userId] });
        queryClient.invalidateQueries({ queryKey: ["homeworkStats", currentUser?.userId]})
        queryClient.invalidateQueries({ queryKey: ["currentHomework", currentUser?.userId]})
      },
    });

    // Resolves true once the submission is saved (or there was nothing to send),
    // false if it failed or one for this task is already in progress. Failures
    // also raise homeworkSubmitFailed so TaskContainer can tell the student.
    const sendHomework = async (percentageCorrect: string, task: Task, submit: any, currentUser: any): Promise<boolean> => {
      if (currentUser?.mode !== 'student' || !task.isHomework) return true;

      const taskKey = String(task.taskId);
      const data = {
        taskId: taskKey,
        submit,
        result: percentageCorrect,
        taskSettings: task.taskSettings
      };
      const locked = localStorage.getItem(`locked_${task.taskId}`) === 'true';
      if (locked) {
        console.warn('Task attempts exhausted, not sending request');
        return true;
      }
      if (inFlight.has(taskKey)) {
        console.warn('Submission already in progress, not sending again');
        return false;
      }

      inFlight.add(taskKey);
      setHomeworkSubmitFailed(false);
      try {
        let response;
        try {
          response = await mutation.mutateAsync(data);
        } catch (error) {
          if (!retryOnNetworkError || !isNetworkError(error)) throw error;
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          response = await mutation.mutateAsync(data);
        }

        const { currentCoins, newCoins } = response;
        setCoinAnimationData({ currentCoins, newCoins });
        setHomeworkSubmitted(true);
        setTimeout(() => setHomeworkSubmitted(false), 2000);
        return true;
      } catch (error: any) {
        const errorData = error.response?.data;

        if (errorData?.error === 'ATTEMPTS_EXHAUSTED') {
          // Persist locally so user won't resend requests
          localStorage.setItem(`locked_${task.taskId}`, 'true');
          setHomeworkSubmitted(false);
        } else {
          console.error('Error message:', errorData?.message ?? error.message);
          setHomeworkSubmitFailed(true);
        }
        return false;
      } finally {
        inFlight.delete(taskKey);
      }
    };

    return { sendHomework, coinAnimationData, ...mutation };
  };
